import type { ReactNode } from 'react';
import React from 'react';
import { parseIngredients } from './helpers/parseIngredients';
import { RecipeProducts } from '../RecipeProducts';
import { DictionaryService, type DictionaryItem } from 'service/dictionary/dictionaryService';
import { Text as Paragraph, Title } from '../Typography';
import clsx from 'clsx';
import { Ingredient } from './components/Ingredient';

export type IngredientType = {
  ArticleSummaryText: string;
  AmountAndUnitSummaryText: string;
  ArticleUnit?: {
    Article?: {
      ProductLink?: string;
      ProductImage?: string;
    };
  };
};

type EquipmentItem = {
  Id: string;
  Title: string;
  TitlePlural: string;
};

type VisibleEquipmentItemPositions = {
  AdditionalText: string;
  EquipmentItem: EquipmentItem;
  Id: string;
  IsAmountVague: boolean;
  SummaryText: string;
};

type IngredientsAndEquipmentBlocks = {
  Title: string;
  Ingredients: IngredientType[];
  Id: string;
  VisibleEquipmentItemPositions: VisibleEquipmentItemPositions[];
};

export type Block = {
  IngredientsAndEquipmentBlocks: IngredientsAndEquipmentBlocks[];
};

interface IIngredientsAndEquipmentProps {
  block: Block;
  copy?: DictionaryItem[];
  trackingEvent?: (data: any) => void;
  hideTabsOnMobile: boolean;
}

export const IngredientsAndEquipmentBlock = ({
  block,
  copy,
  trackingEvent,
  hideTabsOnMobile,
}: IIngredientsAndEquipmentProps): ReactNode => {
  const parsedBlock = parseIngredients(block);

  return (
    <>
      <div className="py-4 mb-4">
        <div className="ingredientWrapper mb-12 block">
          <Title
            className={clsx(
              'mb-9 print:block print:overflow-visible print:h-auto print:max-h-none',
              hideTabsOnMobile ? '' : 'hidden md:block',
            )}
            tag="h2"
            type="md"
          >
            {DictionaryService.getCopy('ingredients', copy)}
          </Title>
          {parsedBlock.map((blockitem, blockIndex) => {
            const equipPositions =
              block.IngredientsAndEquipmentBlocks[blockIndex]?.VisibleEquipmentItemPositions ?? [];
            return (
              <div key={blockitem.title}>
                <Paragraph
                  tag="div"
                  type="lead"
                  className="ingredientText mb-2 mt-10 text-gray-500 print:break-inside-avoid print:relative"
                >
                  {blockitem?.title}
                </Paragraph>
                {blockitem?.ingredients?.map((ingredient, index) => (
                  <Ingredient
                    ingredient={ingredient}
                    trackingEvent={trackingEvent}
                    key={`${ingredient}-${index}`}
                  />
                ))}
                {equipPositions.map((position, idx) => (
                  <React.Fragment key={`equip-${idx}`}>
                    <div className="mb-6 md:my-3">
                      <Paragraph className="relative text-black pl-12" tag="div" type="md">
                        <div className="absolute h-0.5 w-6.5 left-0 top-3 bg-light-brown" />
                        <div dangerouslySetInnerHTML={{ __html: position.SummaryText }} />
                      </Paragraph>
                      {position.AdditionalText && (
                        <Paragraph className="relative text-black pl-12" tag="div" type="md">
                          ({position.AdditionalText})
                        </Paragraph>
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            );
          })}
        </div>
        <div className={hideTabsOnMobile ? 'hidden md:block' : 'block'}>
          <RecipeProducts block={block} copy={copy} trackingEvent={trackingEvent} />
        </div>
      </div>
    </>
  );
};
